import React, { useEffect, useState } from 'react';

import { debounce } from 'lodash';
import { useSelector } from 'react-redux';

import { RootState } from '@share/utils';
import { ILuxuryHome } from '@share/common-types';

import './style.scss';

const DESCRIPTION_ANCHOR = '0';
const HIGHLIGHTS_ANCHOR = '1';
const AMENITIES_ANCHOR = '2';
const LAYOUT_ANCHOR = '3';
const MAP_ANCHOR = '4';
const DEBOUNCE_TIME = 100;

interface IProps {
  home: ILuxuryHome;

  anchorSelected: string;
  setAnchorSelected: (anchor: string) => void;

  descriptionRef: React.RefObject<HTMLElement>;
  highlightsRef: React.RefObject<HTMLElement>;
  amenitiesRef: React.RefObject<HTMLElement>;
  layoutRef: React.RefObject<HTMLElement>;
  mapRef: React.RefObject<HTMLElement>;
}

export const LuxuryHomesDetailsNavigation: React.FC<IProps> = ({ home,anchorSelected, descriptionRef, highlightsRef, amenitiesRef, layoutRef, mapRef, setAnchorSelected }) => {

  const { homes } = useSelector((state: RootState) => state.luxuryHomesStore);

  const [anchorItems, _setAnchorItems] = useState([
    { id: DESCRIPTION_ANCHOR, ref: descriptionRef },
    { id: HIGHLIGHTS_ANCHOR, ref: highlightsRef },
    { id: AMENITIES_ANCHOR, ref: amenitiesRef },
    { id: LAYOUT_ANCHOR, ref: layoutRef },
    { id: MAP_ANCHOR, ref: mapRef },
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);
  
  const onScroll = debounce(() => {
    if (homes) {
      const anchor = getActiveAnchor();

      if (anchor && anchor.id) {
        const index = anchorItems.findIndex(({ id }) => id === anchor.id);

        if (index >= 0) {
          setAnchorSelected(index.toString());
        }
      }
    }
  }, DEBOUNCE_TIME);

  const getActiveAnchor = () => {
    const anchors: any[] = anchorItems;
    const { pageYOffset } = window;
    const additionalHeight = 300;
    let activeRef = anchors[0];

    anchors.forEach((anchor) => {
      const { ref } = anchor;

      if (ref && ref.current) {
        const { current } = ref;
        const height = current.getBoundingClientRect()?.height;
        const offsetTop = current.offsetTop - additionalHeight;
        const offsetBottom = offsetTop + height;

        if (height && offsetTop && pageYOffset >= offsetTop && pageYOffset <= offsetBottom) {
          activeRef = anchor;
        }
      }
    });

    return activeRef;
  };

  const scrollView = (index: string, ref: React.RefObject<HTMLElement>) => {
    setAnchorSelected(index);
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'end' });
  };

  const renderAnchor = (display: boolean, anchor: string, title: string, ref: any) => {

    const className = anchorSelected === anchor ? 'selected' : '';
    if (display) {
      return (
        <li 
          className={`property-nav__item ${className}`}
          onClick={() => scrollView(anchor, ref)}
        >
          <div>{title}</div>
        </li>);
    }

    return null;
  };

  const displayHighlights = !!home?.activities?.length;

  return (  
    <nav className="property-nav">
      <ul>
        {renderAnchor(true, DESCRIPTION_ANCHOR, 'Description', descriptionRef)}

        {renderAnchor(displayHighlights, HIGHLIGHTS_ANCHOR, 'Highlights', highlightsRef)}

        {renderAnchor(true, AMENITIES_ANCHOR, 'Amenities', amenitiesRef)}

        {renderAnchor(true, LAYOUT_ANCHOR, 'Layout', layoutRef)}

        {renderAnchor(true, MAP_ANCHOR, 'Map', mapRef)}
      </ul>
    </nav>
  );
};
